import { Signal } from '@angular/core';
import { Question, Section } from '@data-access/shared';
import { EventCreator } from '@ngrx/signals/events';
import type {
  SDAttributeType,
  SDDocumentType,
} from '@data-access/documents/types/';
import { MarkStatus } from '@submissions/v1/models';

export type TaskType =
  | 'qc_mark_required'
  | 'sme_mark_required'
  | 'document_conditionally_required'
  | 'document_type_required'
  | 'question'
  | 'section'
  | 'document'
  | 'document_type_requested';

/** Represents the known types for any given task entity **/
type TaskEntityType = 'question' | 'section' | 'document';

/** A task entity is some object related to a task. */
export type TaskEntity = {
  /** ID of the taskentity (question, section, or document type) */
  id: string;
  /** Type of the task entity (question, section, or document) */
  type: TaskEntityType;
  /** ID of the question associated with the task entity */
  question_id: string | null;
  /** ID of the section associated with the task entity */
  section_id: string | null;
  /** ID of the document type associated with the task entity */
  document_id: string | null;
};

/** All valid task status values as a const array */
export const ALL_TASK_STATUSES = [
  'new_task',
  'document_uploaded',
  'ready_for_review',
  'incomplete',
  're_opened',
  'review_completed',
  'cancelled',
] as const;

/** Valid task types for external users "to do" */
export const EXTERNAL_TODO_TASK_STATUSES: TaskStatus[] = [
  'new_task',
  'incomplete',
  're_opened',
] as const;

/** Valid task types for internal users "to do" */
export const INTERNAL_TODO_TASK_STATUSES: TaskStatus[] = [
  'document_uploaded',
  'ready_for_review',
] as const;

/** Represents the known status for any given task instance */
export type TaskStatus = (typeof ALL_TASK_STATUSES)[number];

export type NonCancelledTaskStatus = Exclude<TaskStatus, 'cancelled'>;

/** All valid non-cancelled task status values as a const array */
export const NON_CANCELLED_TASK_STATUSES = ALL_TASK_STATUSES.filter(
  (status) => status !== 'cancelled',
) as readonly NonCancelledTaskStatus[];

/** Represents the shape of a task assignee */
export type Assignee = {
  /** Primary key of the user assigned to a task */
  id: string;
  /** Legacy ID of the user assigned to a task */
  legacy_id?: number;
  /** Email of the user assigned to a task */
  email: string;
  /** First name of the user assigned to a task */
  first_name: string;
  /** Last name of the user assigned to a task */
  last_name: string;
  /** Whether the user is internal */
  is_internal: boolean;
};

export type TaskDocument = {
  id: string;
  name: string;
  wcg_name: string;
  title: string;
  entity_id: string;
  created_dt: string;
  updated_dt: string;
  is_completed?: boolean;
  complete_timestamp?: string;
  created_by_user?: Pick<
    Assignee,
    'id' | 'email' | 'first_name' | 'last_name' | 'is_internal'
  >;
  document_id?: string;
  attribute_type?: SDAttributeType;
  document_type?: SDDocumentType;
  document_type_id: string;
  attribute_type_id?: string;
  document_status?: 'pending' | 'failed' | 'completed';
  document_description?: string;
};

/** Represents the shape of a request body for searching tasks */
export type TSearchTaskInstanceRequest = {
  /** List of task instance IDs to filter by */
  ids?: Array<string>;
  /** Title of the task instance */
  title?: string;
  /** Description of the task instance */
  description?: string;
  /**
   * Task types to filter by, can include:
   * - Individual TaskType values
   * - 'document_tasks' as shorthand for all document-related task types
   */
  task_types?: Array<TaskType | 'document_tasks'>;
  /** List of status to filter task instance by */
  status?: Array<TaskStatus>;
  /** User id of the active task assignee */
  active_assignee_id?: string;
  /** List of user ids to exclude from assignee filter */
  exclude_active_assignee_ids?: Array<string>;
  /** Filter by internal-only tasks */
  is_internal?: boolean;
  /** Filter by completion status */
  is_completed?: boolean;
  /** Question ID to filter task associated with a specific question */
  question_id?: string;
  /** Section ID to filter task associated with a specific section */
  section_id?: string;
  /** Document ID to filter task associated with a specific document */
  document_id?: string;
  /** Default document type ID to filter tasks by */
  default_document_type_id?: string;
};

/**
 * Represents the shape of the request body for querying a list of tasks
 * as dropdown items
 */
export type TTaskListDropdownQueryParams = TSearchTaskInstanceRequest & {
  /** Whether to return the response of found tasks as dropdown items - always true */
  readonly response_as_dropdown: true;
};

/** Represents the shape of the request body for assigning a task to a user */
export type TAssignTaskRequest = {
  /** The primary key of the user to assign a task to. If Undefined, the task will be unassigned. */
  assignee_id?: string;
};

/** Represents the shape of the request body for updating the status of a task instance */
export type TUpdateTaskStatusRequest = {
  /** The new status to set for the task instance */
  status: TaskStatus;
};

export type TBulkUpdateTaskStatusRequest = {
  /** List of task instance IDs to update */
  task_instance_ids: Array<string>;
  /** The new status to set for the task instances */
  to_status: TaskStatus;
};

/** Represents the shape of the request body for creating a task instance */
type TBaseCreateTaskRequest = {
  /** The title of the task to create */
  title: string;
  /** The description of the task to create */
  description: string;
  /**
   * The primary key of the user to assign a task to.
   * If Undefined, the task will be unassigned.
   */
  assign_to_id?: string;
  /** Whether the task is internal */
  is_internal?: boolean;
};

/** Represents the shape of the request body for creating a task instance for a question */
export type TCreateQuestionTaskRequest = TBaseCreateTaskRequest & {
  /** The primary key of the question to create the task for */
  question_id: string;
};

/** Represents the shape of the request body for creating a task instance for a section */
export type TCreateSectionTaskRequest = TBaseCreateTaskRequest & {
  /** The primary key of the section to create the task for */
  section_id: string;
};

/** Fundamental (shared) properties returned for a given task instance item */
export type TaskInstanceDetail = {
  /** Primary key of the task instance item */
  id: string;
  /** Title of the task instance item */
  title?: string;
  /** Description of the task instance item */
  description?: string;
  /** Primary key of the task template that this task was created from */
  task_template_id?: string;
  /** Type of the task instance item */
  task_type: TaskType;
  /** Primary key of the workspace that this task was created in */
  workspace_id: string;
  /** The current status of this task instance */
  status: TaskStatus;
  /** Primary key of the user that is currently assigned to this task */
  active_assignee_id?: string;
  /** The user that is currently assigned to this task */
  active_assignee?: Assignee;
  /** Whether this task is internal */
  is_internal: boolean;
  /** Whether this task is completed */
  is_completed: boolean;
  /** The date and time that this task was completed */
  completed_dt?: string;
  /** Primary key of the user that completed this task */
  completed_by_id?: string;
  /** The date and time that this task was created */
  created_dt?: string;
  /** The date and time that this task was updated */
  updated_dt?: string;
  /** Primary key of the document type that the task is associated with */
  document_type_id?: string;
};

/** Represents the shape of a thread associated with a task instance */
export type TaskInstanceThread = {
  /** Primary key of the thread associated with this task instance */
  id: string;
  /** Workspace ID that this thread belongs to */
  workspace_id: string;
  /** Entity id that this thread is associated with */
  entity_id: string;
  /** Entity type that this thread is associated with */
  entity_type: string;
  /** Whether the thread has been resolved */
  is_resolved: boolean;
  /** Whether the thread has been closed */
  is_closed: boolean;
  /** The date and time that this thread was last updated */
  updated_dt: string;
  /** The source location where this thread was created */
  source_location: string;
  /** The date and time that this thread was created */
  created_dt: string;
};

/** Represents the shape of remaining tasks categorized by their type */
export type TRemainingTasks = {
  /** A list of document tasks that are remaining */
  document: TaskInstance[];
  /** A list of question tasks that are remaining */
  question: TaskInstance[];
  /** A list of section tasks that are remaining */
  section: TaskInstance[];
  /** A list of other tasks that are remaining but their categorization is unknown */
  other: TaskInstance[];
};

/** Represents the shape of response when querying for a list of tasks associated to a given workspace */
export type TaskInstance = TaskInstanceDetail & {
  /** The entity that the task is associated with */
  task_entity?: TaskEntity;
  /** The thread that the task is associated with */
  thread?: TaskInstanceThread;
  /** The last message in the thread associated with this task */
  last_message?: ThreadMessage;
  /** The number of messages in the thread */
  message_count?: number;
};

export type TTaskInstanceDropdownResponse = {
  /** Primary key of the task instance item */
  id: string;
  /** Title of the task instance item */
  title?: string;
  /** Description of the task instance item */
  description?: string;
};

/** Represents the shape of response when querying for a task instance */
export type TTaskInstanceDetailResponse = TaskInstanceDetail & {
  /** The entity that the task is associated with */
  task_entity?: TaskEntity;
  /**
   * A list of documents that correspond with the task (related by task entity)
   */
  documents?: Array<TaskDocument>;
  /**
   * A list of sections that correspond with the task (related by task entity)
   */
  sections?: Array<Section>;
  /**
   * A list of questions that correspond with the task (related by task entity)
   */
  questions?: Array<Question>;
  /**
   * Thread associated with the task instance
   **/
  thread?: TaskInstanceThread;
  /**
   * The last message in the thread associated to the task
   **/
  last_message?: ThreadMessage;

  /**
   * message count in the thread associated to the task
   **/
  message_count?: number;
};

export type TaskListState = {
  error: Error | null;
  filtered: boolean;
  isSMEQCLoading: boolean;
  loading: boolean;
  /**
   * A Record of remaining tasks categorized by their type
   */
  remainingSMEQCTasks: TRemainingTasks;
  /**
   * A record of remaining todos categorized by their type for external collabhub messaging
   */
  remainingToDoTasks: TRemainingTasks;
  /**
   * A list of task instances which populate the main task list
   * for a given workspace
   */
  tasks: TaskInstance[];
  tasksAlreadyLoaded: boolean;
};

export type TaskListType = 'document' | 'section' | 'question';
export type UpdateTaskInListPayload = {
  taskInstanceId: string;
  task: TaskInstance;
  taskListType: TaskListType;
  hasError?: boolean;
};

export type UpdateTaskStatusPayload = {
  workspaceId: string;
  taskInstanceId: string;
  taskStatus: TaskStatus;
  taskListType: TaskListType;
};

export type BulkUpdateTaskStatusPayload = {
  workspaceId: string;
  taskInstanceIds: string[];
  taskStatus: TaskStatus;
  taskListType: TaskListType;
};

export type BulkUpdateTaskStatusByIdsPayload = {
  taskIds: string[];
  isCompleted: boolean;
  taskListType: TaskListType;
};

export type SMEQCTaskListPayload = {
  workspaceId: string;
  taskType: TaskType[];
  isCompleted: boolean | undefined;
  taskListType?: TaskListType;
  ids?: string[];
};

export type TaskEvents = {
  getTaskList: EventCreator<
    '[Task Signal Store] getTaskList',
    {
      workspaceID: string;
      submissionStage: string;
    }
  >;

  /**
   * @NOTE: Get remaining tasks count categorized by their type for a given workspace
   */
  getRemainingToDoTaskCount: EventCreator<
    '[Task Signal Store] getRemainingToDoTaskCount',
    {
      workspaceID: string;
    }
  >;

  /**
   * @NOTE: Update the filtered state of the task list
   **/
  updateTaskListFiltered: EventCreator<
    '[Task Signal Store] updateTaskListFiltered',
    { filtered: boolean }
  >;

  getSMEQCTaskList: EventCreator<
    '[Task Signal Store] getSMEQCTaskList',
    SMEQCTaskListPayload
  >;

  getSMEQCTaskListByIds: EventCreator<
    '[Task Signal Store] getSMEQCTaskListByIds',
    SMEQCTaskListPayload
  >;

  patchTaskList: EventCreator<
    '[Task Signal Store] patchTaskList',
    {
      taskList: TaskInstance[];
    }
  >;

  patchDocumentTaskList: EventCreator<
    '[Task Signal Store] patchDocumentTaskList',
    { taskList: TaskInstance[] }
  >;

  /*
   * @NOTE: Patch the task list for section-related tasks
   * */
  patchSectionTaskList: EventCreator<
    '[Task Signal Store] patchSectionTaskList',
    {
      taskList: TaskInstance[];
    }
  >;
  /*
   * @NOTE: Patch the task list for question-related tasks
   * */
  patchQuestionTaskList: EventCreator<
    '[Task Signal Store] patchQuestionTaskList',
    {
      taskList: TaskInstance[];
    }
  >;
  /*
   * @NOTE: Update SME/QC mark status for a specific section within a workspace
   * */
  updateSMEQCMARK: EventCreator<
    '[Task Signal Store] updateSMEQCMARK',
    { workspaceId: string; sectionId: string; markStatus: MarkStatus }
  >;
  /*
   * @NOTE: Update the status of a specific task instance within a workspace
   * */
  updateTaskStatus: EventCreator<
    '[Task Signal Store] updateTaskStatus',
    UpdateTaskStatusPayload
  >;

  /*
   * @NOTE: Update multiple task instances' status within a workspace
   * */
  bulkUpdateTaskStatus: EventCreator<
    '[Task Signal Store] bulkUpdateTaskStatus',
    BulkUpdateTaskStatusPayload
  >;
  bulkUpdateTaskStatusByIds: EventCreator<
    '[Task Signal Store] bulkUpdateTaskStatusByIds',
    BulkUpdateTaskStatusByIdsPayload
  >;

  /*
   * @NOTE; Update individual task instance in the list basing on task-instanceId and taskListType
   * */
  updateTaskInList: EventCreator<
    '[Task Signal Store] updateTaskInList',
    UpdateTaskInListPayload
  >;

  /*
   * @NOTE: Get remaining tasks categorized by their type for a given workspace
   * */
  exportAllThreads: EventCreator<
    '[Task Signal Store] exportAllThreads',
    {
      workspaceID: string;
    }
  >;
  /*
   * @NOTE: Reset the task list state to its initial values
   * */
  resetTaskListState: EventCreator<
    '[Task Signal Store] resetTaskListState',
    void
  >;
};

export type TaskDetailEvents = {
  addEntityToTask: EventCreator<
    '[Task Detail Signal Store] addEntityToTask',
    {
      data: unknown;
      workspaceID: string;
    }
  >;
  bulkUpdateTaskStatus: EventCreator<
    '[Task Detail Signal Store] bulkUpdateTaskStatus',
    {
      workspaceID: string;
      taskInstanceIds: string[];
      toStatus: TaskStatus;
    }
  >;
  clearTaskDetail: EventCreator<
    '[Task Detail Signal Store] clearTaskDetail',
    void
  >;
  createDocumentTask: EventCreator<
    '[Task Detail Signal Store] createDocumentTask',
    {
      workspaceID: string;
      payload: DocumentTaskCreatedPayload;
    }
  >;
  createQuestionTask: EventCreator<
    '[Task Detail Signal Store] createQuestionTask',
    {
      workspaceID: string;
      payload: QuestionTaskCreatedPayload;
    }
  >;
  createSectionTask: EventCreator<
    '[Task Detail Signal Store] createSectionTask',
    {
      workspaceID: string;
      payload: SectionTaskCreatedPayload;
    }
  >;
  createTaskMessage: EventCreator<
    '[Task Detail Signal Store] createTaskMessage',
    {
      workspaceID: string;
      taskInstanceId: string;
      message: MessagePayload;
    }
  >;
  exportThreadMessages: EventCreator<
    '[Task Detail Signal Store] exportThreadMessages',
    {
      workspaceID: string;
      threadId: string;
    }
  >;
  getTaskDetail: EventCreator<
    '[Task Detail Signal Store] getTaskDetail',
    {
      workspaceID: string;
      taskInstanceId: string;
    }
  >;
  getTaskMessages: EventCreator<
    '[Task Detail Signal Store] getTaskMessages',
    {
      workspaceID: string;
      threadId: string;
    }
  >;
  patchMessages: EventCreator<
    '[Task Detail Signal Store] patchMessages',
    {
      messages: ThreadMessage[];
    }
  >;
  patchTaskDetail: EventCreator<
    '[Task Detail Signal Store] patchTaskDetail',
    {
      taskDetail: TaskInstanceDetail | null;
      error: Error | null;
      messageCommunicationEstablished: boolean;
      lastRefreshedTaskId: string;
    }
  >;
  refreshTaskDetail: EventCreator<
    '[Task Detail Signal Store] refreshTaskDetail',
    { workspaceId: string; taskDetailId: string }
  >;
  searchDocumentTask: EventCreator<
    '[Task Detail Signal Store] searchDocumentTask',
    {
      workspaceID: string;
      payload: DocumentTaskCreatedPayload;
    }
  >;
  searchQuestionTask: EventCreator<
    '[Task Detail Signal Store] searchQuestionTask',
    {
      workspaceID: string;
      payload: QuestionTaskCreatedPayload;
    }
  >;
  searchSectionTask: EventCreator<
    '[Task Detail Signal Store] searchSectionTask',
    {
      workspaceID: string;
      payload: SectionTaskCreatedPayload;
    }
  >;
  updateTaskStatus: EventCreator<
    '[Task Detail Signal Store] updateTaskStatus',
    {
      status: TaskStatus;
      taskInstanceId: string;
      workspaceID: string;
    }
  >;
};

export type TaskListSignalState = {
  /** Computes any tasks with the type matching a known DOCUMENT_TASK type */
  $documentTasks: Signal<TaskInstance[]>;
  $error: Signal<Error | null>;
  $filtered: Signal<boolean>;
  $isLoading: Signal<boolean>;
  /** Loading state for smeqc toggles*/
  $isSMEQCLoading: Signal<boolean>;
  /** Computes any tasks with the 'question' task entity type */
  $questionTasks: Signal<TaskInstance[]>;
  /** Computes any remaining 'document' SME QC tasks (internal, not completed) */
  $remainingDocumentSMEQCTasks: Signal<TaskInstance[]>;
  /** Computes any remaining 'other' SME QC tasks (internal, not completed) */
  $remainingOtherSMEQCTasks: Signal<TaskInstance[]>;
  /** Computes any remaining 'question' SME QC tasks (internal, not completed) */
  $remainingQuestionSMEQCTasks: Signal<TaskInstance[]>;
  /** Computes any remaining SME QC tasks (internal, not completed) */
  $remainingSMEQCTasks: Signal<TRemainingTasks>;
  /**Get Submission Detail Section Todo Task Count  */
  $remainingSectionTaskTodoCount: Signal<Map<string, number>>;
  /** Computes any remaining 'section' SME QC tasks (internal, not completed) */
  $remainingSectionSMEQCTasks: Signal<TaskInstance[]>;
  /** Get Submission Detail remaining to-do tasks (external, not completed) count */
  $remainingToDoDetailTaskCount: Signal<number>;
  /** Get Document remaining to-do tasks (external, not completed) count */
  $remainingToDoDocumentTaskCount: Signal<number>;
  /** Computes any remaining to-do collabhub tasks (external, not completed) */
  $remainingToDoCollabHubTasks: Signal<TRemainingTasks>;
  /** Computes remaining document tasks (type 'document') as a Map */
  $remainingDocumentToDoTasks: Signal<Map<string, TaskInstance>>;
  /** Computes remaining requested document tasks as a Map */
  $remainingRequestedDocumentToDoTasks: Signal<Map<string, TaskInstance>>;
  /** Computes remaining question tasks (type 'question') as a Map */
  $remainingQuestionToDoTasks: Signal<Map<string, TaskInstance>>;
  /** Computes remaining section tasks (type 'section') as a Map */
  $remainingSectionToDoTasks: Signal<Map<string, TaskInstance>>;
  /** Computes any tasks with the 'section' task entity type */
  $sectionTasks: Signal<TaskInstance[]>;
  /** Computes all fetched tasks */
  $tasks: Signal<TaskInstance[]>;
  /** Computes all tasks that are associated with a collabhub thread */
  $threads: Signal<TaskInstance[]>;
  $tasksAlreadyLoaded: Signal<boolean>;
};

export type TaskDetailState = {
  taskDetail: TaskInstanceDetail | null;
  messages: ThreadMessage[];
  loadingTaskDetail: boolean;
  loadingMessages: boolean;
  error: Error | null;
  filteredTask: boolean;
  lastRefreshedTaskId: string | null;
  messageCommunicationEstablished: boolean;
};

export type TaskDetailSignalState = {
  $error: Signal<Error | null>;
  $exportText: Signal<string>;
  $filteredTask: Signal<boolean>;
  $isLoadingMessages: Signal<boolean>;
  $isLoadingTaskDetail: Signal<boolean>;
  $lastRefreshedTaskId: Signal<string | null>;
  $messageCommunicationEstablished: Signal<boolean>;
  $messages: Signal<ThreadMessage[]>;
  $taskDetail: Signal<TTaskInstanceDetailResponse | null>;
  updateTaskDetailFilteredState: (filteredTask: boolean) => void;
};

export type TaskBannerSize = 'full' | 'half' | 'auto';

export type TaskSelectBoxSize =
  | TaskBannerSize
  | 'compact'
  | 'medium'
  | 'large'
  | 'x-large';

/** Represents the shape of a thread message */
export type ThreadMessage = {
  /** Primary key of the thread message */
  id: string;
  /** Sender of the message */
  sender: Assignee;
  /** text content of the message */
  text: string;
  /** sent timestamp of the message */
  sent_timestamp: string;
  /** any additional data related to the message */
  data: Record<string, unknown>;
  /** whether the message has been deleted */
  is_deleted: boolean;
  /** timestamp on when the message has been created */
  created_dt: string;
  /** timestamp on when the message has been updated */
  updated_dt: string;
  /** whether the thread message has been resolved */
  is_resolved: boolean | null;
};

/** Represents the payload required to create a new message */
export type MessagePayload = {
  /** The text content of the message */
  text: string;
  /** Any additional data related to the message */
  data: Record<string, unknown>;
  /** Whether a response is required for the message */
  response_required?: boolean;
};

/** Represents the default payload required to create a task */
export type DefaultTaskCreatedPayload = {
  /** The title of the task to create */
  title: string;
  /** The description of the task to create */
  description?: string;
  /** assign to user id */
  assign_to_id?: string;
  /** Whether the task is internal */
  is_internal?: boolean;
  /** The ID of the document to associate the task with */
};

/** Represents the payload required to create a document-related task */
export type DocumentTaskCreatedPayload = DefaultTaskCreatedPayload & {
  /** The ID of the document to associate the task with */
  document_id: string;
};

/** Represents the payload required to create a section-related task */
export type SectionTaskCreatedPayload = DefaultTaskCreatedPayload & {
  /** The ID of the section_id to associate the task with */
  section_id: string;
};

/** Represents the payload required to create a section-related task */
export type QuestionTaskCreatedPayload = SectionTaskCreatedPayload & {
  /** The ID of the question_id to associate the task with */
  question_id: string;
};

/** Represents a user in the system */
export type User = {
  /** First name of the user */
  first_name: string;
  /** Last name of the user */
  last_name: string;
  /** Whether the user is internal */
  is_internal: boolean;
};
export type MessageResponse = {
  /** The ID of the message **/
  id: string;
  /** The text content of the message **/
  text: string;
  /** The workspace ID the message belongs to **/
  workspace_id: string | null;
  /** The parent ID of the message **/
  parent_id?: string;
  /** The entity ID the message is associated with **/
  entity_id: string;
  /** The entity type the message is associated with **/
  entity_type: 'task_instance';
  /** Whether the message has been resolved **/
  is_resolved: boolean | null;
  /** Additional data related to the message **/
  data: Record<string, unknown>;
  /** Sender of the message which has more information like name and access level **/
  sender: User;
  /** The date and time the message was created **/
  created_dt: string;
  /** The date and time the message was last updated **/
  updated_dt: string;
  /** The timestamp when the message was sent **/
  sent_timestamp: string;
  /** The timestamp when the message was saved as a draft **/
  draft_timestamp: string;
  /** The ID of the user who resolved the message **/
  resolved_by_id: string;
  /** The user who resolved the message **/
  resolved_by: string;
  /** Whether a response is required for the message **/
  respond?: boolean;
  /** The timestamp when the message was acknowledged **/
  acknowledged_at?: string;
  /** The user who acknowledged the message **/
  acknowledged_by?: string;
  /** The ID of the thread the message belongs to **/
  thread_id?: string;
};
